import * as THREE from 'three';
import React, {useEffect, useMemo, useRef, useState} from 'react'
import bgVert from './bg-vert'
import bgFrag from './bg-frag'
import { useFx, useSize, useCommon, imgLoading, textureLoading } from './fx.js'
import { MouseTail } from './mouse-tail.js'
import { MouseParticles } from './mouse-particles.js'
import { MouseDirect } from './mouse-direct.js'
import { MouseDistort } from './mouse-distort.js'
import tools from "../tools/tools.js"
const defPosition = new THREE.Vector3(0,0,0)

function Background({readyFun, layers=0, position=defPosition, isBokeh=false}) {
	const fx = useFx();
	const size = useSize()
	const {camera, frameRun, scene} = fx
	const pCount = 50
	const isZoomOnScroll = !fx.bgAnimation
	const isZoomInfinity = fx.bgAnimation === 'zoomInfinity'
	const isZoomNo = fx.bgAnimation === 'no'
	const isParticles = (fx.pointer === 'particles') && fx.colorB
	const isDistort = (fx.pointer === 'distort') && (fx.imgA)
	const isNoMouse = fx.pointer === 'no'
	const isBgImg = fx.imgA || fx.imgAMedium || fx.imgASmall
	const isTail = (fx.pointer === 'trail' || !fx.pointer) && (fx.colorB || isBgImg)
	const isMouseBuffer = isTail || isParticles || isDistort
	const isMouseEffect = isMouseBuffer
	const isMouseColoredEffect = isTail || isParticles
	const [material, setMaterial] = useState(false)
	const [geometry, setGeometry] = useState(false)
	const [imgA, setImgA] = useState(false)
	const [effectTexture, setEffectTexture] = useState(false)
	const [bgTexture, setBgTexture] = useState(false)
	const imgAW = fx['imgA-width']
	const imgAH = fx['imgA-height']
	const imgAMediumW = fx['imgAMedium-width']
	const imgAMediumH = fx['imgAMedium-height']
	const imgASmallW = fx['imgASmall-width']
	const imgASmallH = fx['imgASmall-height']
	const defines = useMemo(()=>{
		const res = {}
		if(isMouseColoredEffect){
			res.USE_MOUSE_COLORED_EFFECT = ''
		}
		if(isTail){
			res.POINTER_TAIL = ''
		}else if(isDistort){
			res.POINTER_DISTORT = ''
		}else if(isParticles){
			res.POINTER_PARTICLES = ''
		}
		if(isMouseBuffer){
			res.USE_MOUSE_BUFFER = ''
		}
		if(isBgImg){
			res.USE_BG_TEXTURE = ''
		}
		if(fx.bokeh){
			res.USE_BOKEH = ''
		}
		if(fx.gradient){
			res.USE_GRADIENT = ''
		}
		if(fx.colorA || fx.colorAA || fx.colorAB ){
			if(fx.mixMode === 'darken'){
				res.MIX_DARKEN = ''
			}else if(fx.mixMode === 'lighten'){
				res.MIX_LIGHTEN = ''
			}else if(fx.mixMode === 'difference'){
				res.MIX_DIFFERENCE = ''
			}else if(fx.mixMode === 'multiply'){
				res.MIX_MULTIPLY = ''
			}else if(fx.mixMode === 'color'){
				res.MIX_COLOR = ''
			}else if(fx.mixMode === 'normal'){
				res.MIX_NORMAL = ''
			}else{
				res.MIX_NO = ''
			}
		}else{
			res.MIX_NO = ''
		}
		if(fx.colorB){
			if(fx.pointerMixMode === 'darken'){
				res.MOUSE_MIX_DARKEN = ''
			}else if(fx.pointerMixMode === 'lighten'){
				res.MOUSE_MIX_LIGHTEN = ''
			}else if(fx.pointerMixMode === 'difference'){
				res.MOUSE_MIX_DIFFERENCE = ''
			}else if(fx.pointerMixMode === 'multiply'){
				res.MOUSE_MIX_MULTIPLY = ''
			}else if(fx.pointerMixMode === 'color'){
				res.MOUSE_MIX_COLOR = ''
			}else if(fx.pointerMixMode === 'normal'){
				res.MOUSE_MIX_NORMAL = ''
			}else{
				res.MOUSE_MIX_NO = ''
			}
		}else{
			res.MOUSE_MIX_NO = ''
		}
		if(isZoomOnScroll){
			res.ZOOM_ON_SCROLL = ''
		}else if(isZoomInfinity){
			res.ZOOM_INFINITY = ''
		}else if(isZoomNo){
			res.ZOOM_NO = ''
		}
		if(isBokeh){
			res.USE_BOKEH = ''
		}
		return res
	},[isTail, isParticles, isMouseBuffer, fx.mixMode, fx.bokeh, fx.pointerMixMode, fx.gradient,
		isZoomOnScroll, isZoomInfinity, isZoomNo, isBokeh, isDistort, isMouseColoredEffect, isBgImg, fx.colorA, fx.colorAA, fx.colorAB, fx.colorB])
	useEffect(()=>{
		const uniforms = {
			resolution: {value: new THREE.Vector2(0, 0)},
			bgColor: {value: new THREE.Color(0xffffff)},
			bgColorHSL: {value: new THREE.Vector3(0,0,1)},
			effectColor: {value: new THREE.Color(0xffffff)},
			effectColorHSL: {value: new THREE.Vector3(0,0,1)},
			bgTexture: {value: null},
			bgTextureRuv: {value: new THREE.Vector2(1, 1)},
			bgTextureDuv: {value: new THREE.Vector2(0, 0)},
			effectTexture: {value: null},
			effectTextureRuv: {value: new THREE.Vector2(1, 1)},
			effectTextureDuv: {value: new THREE.Vector2(0, 0)},
			time: {value: 0},
			sFactor: {value: 0},
			gradientW: {value: .5},
			zoom: {value: 1},
			zoom1: {value: 1},
			zoom2: {value: 1},
			zoomMix: {value: 0},
			dx1: {value: 0},
			dy1: {value: 0},
			dx2: {value: 0},
			dy2: {value: 0},
			mouseTexture: {value: null},
			pCount: {value: pCount},
			bokehSize: {value: 1},
			bokehGlow: {value: 1},
			bokehColorA: {value: null},
			bokehColorB: {value: null},
			bokehColorC: {value: null},
			bokehColorD: {value: null},
			bokehColorE: {value: null}
		}
		const mat = new THREE.ShaderMaterial({
			defines,
			uniforms,
			vertexShader: bgVert,
			fragmentShader: bgFrag
		})
		const geom = new THREE.BufferGeometry()
		const mesh = new THREE.Mesh(geom, mat)
		mesh.layers.set(layers)
		mesh.position.set(position.x, position.y, position.z)
		scene.add(mesh)
		setGeometry(geom)
		setMaterial(mat)
		return ()=>{
			scene.remove(mesh)
			geom.dispose()
			mat.dispose()
		}
	},[isMouseBuffer, isParticles, defines, position.x, position.y, position.z, layers, scene,
	   isZoomInfinity, isZoomOnScroll, fx.bokeh])
	useEffect(() => {
		if(material){
			material.uniforms.effectColor.value = new THREE.Color(fx.colorB ? fx.colorB : 0xffffff)
			const hsl = {}
			material.uniforms.effectColor.value.getHSL(hsl)
			material.uniforms.effectColorHSL.value = new THREE.Vector3(hsl.h, hsl.s, hsl.l)
		}
	}, [fx.colorB, material])
	useEffect(() => {
		if(material && material.uniforms.bokehColorA && material.uniforms.bokehColorB && material.uniforms.bokehColorC && material.uniforms.bokehColorD && material.uniforms.bokehColorE){
			let bc = []
			if(fx.bokehColorA) bc.push(new THREE.Color(fx.bokehColorA))
			if(fx.bokehColorB) bc.push(new THREE.Color(fx.bokehColorB))
			if(fx.bokehColorC) bc.push(new THREE.Color(fx.bokehColorC))
			if(fx.bokehColorD) bc.push(new THREE.Color(fx.bokehColorD))
			if(fx.bokehColorE) bc.push(new THREE.Color(fx.bokehColorE))
			if(bc.length < 1){
				bc = [
					new THREE.Vector3(0.4, 0.1, 0.2),
					new THREE.Vector3(0.7, 0.467, 0.233),
					new THREE.Vector3(0.4, 0.3, 0.2),
					new THREE.Vector3(0.4, 0.2, 0.1),
					new THREE.Vector3(0.2, 0.0, 0.4)
				]
			}
			const bu = [
				material.uniforms.bokehColorA,
				material.uniforms.bokehColorB,
				material.uniforms.bokehColorC,
				material.uniforms.bokehColorD,
				material.uniforms.bokehColorE,
			]
			let bcInd = 0
			bu.forEach((v)=>{
				if(bcInd === bc.length){
					bcInd = 0
				}
				v.value = bc[bcInd]
				bcInd++
			})
		}
	}, [material, fx.bokehColorA, fx.bokehColorB, fx.bokehColorC, fx.bokehColorD, fx.bokehColorE ])
	useEffect(() => {
		if(material && material.uniforms.bokehSize){
			material.uniforms.bokehSize.value = fx.bokehSize ? fx.bokehSize : 1
		}
	}, [fx.bokehSize, material])
	useEffect(() => {
		if(material && material.uniforms.bokehGlow){
			material.uniforms.bokehGlow.value = fx.bokehGlow ? fx.bokehGlow : 1
		}
	}, [fx.bokehGlow, material])
	useEffect(() => {
		if(material && material.uniforms.gradientW){
			material.uniforms.gradientW.value = fx.gradientW ? fx.gradientW / 100 : .5
		}
	}, [fx.gradientW, material])
	useEffect(() => {
		if(material && bgTexture){
			bgTexture.minFilter = THREE.LinearFilter // May be it is not needed for WebGL 2, but needed for WebGL 1
			material.uniforms.bgTexture.value = bgTexture
			const textureARatio = bgTexture.image.width / bgTexture.image.height;
			let rx, ry, dx, dy;
			const ratio =  size.width / size.height;
			if(textureARatio > ratio){
				rx = ratio/textureARatio;
				ry = 1;
				dx = (1 - rx)/2;
				dy = 0;
			}else{
				ry = textureARatio/ratio;
				rx = 1;
				dy = (1 - ry)/2;
				dx = 0;
			}
			material.uniforms.bgTextureRuv.value.x = rx;
			material.uniforms.bgTextureRuv.value.y = ry;
			material.uniforms.bgTextureDuv.value.x = dx;
			material.uniforms.bgTextureDuv.value.y = dy;
		}
	}, [material, bgTexture, size.width, size.height])
	useEffect(() => {
		if(material && effectTexture){
			effectTexture.minFilter = THREE.LinearFilter // May be it is not needed for WebGL 2, but needed for WebGL 1
			material.uniforms.effectTexture.value = effectTexture
			const textureBRatio = effectTexture.image.width / effectTexture.image.height;
			let rx, ry, dx, dy;
			const ratio =  size.width / size.height;
			if(textureBRatio > ratio){
				rx = ratio/textureBRatio;
				ry = 1;
				dx = (1 - rx)/2;
				dy = 0;
			}else{
				ry = textureBRatio/ratio;
				rx = 1;
				dy = (1 - ry)/2;
				dx = 0;
			}
			material.uniforms.effectTextureRuv.value.x = rx;
			material.uniforms.effectTextureRuv.value.y = ry;
			material.uniforms.effectTextureDuv.value.x = dx;
			material.uniforms.effectTextureDuv.value.y = dy;
		}
	}, [material, effectTexture, size.width, size.height])
	useEffect(() => {
		const resize = ()=>{
			const ww = getComputedStyle(document.body).getPropertyValue('--airtheme-window-width');
			let url
			let w
			let h
			if(ww == 1){
				if(fx.imgAMedium){
					url = fx.imgAMedium
					w = imgAMediumW
					h = imgAMediumH
				}else if(fx.imgA){
					url = fx.imgA
					w = imgAW
					h = imgAH
				}else{
					url = fx.imgASmall
					w = imgASmallW
					h = imgASmallH
				}
			}else if(ww == 2){
				if(fx.imgASmall){
					url = fx.imgASmall
					w = imgASmallW
					h = imgASmallH
				}else if(fx.imgAMedium){
					url = fx.imgAMedium
					w = imgAMediumW
					h = imgAMediumH
				}else{
					url = fx.imgA
					w = imgAW
					h = imgAH
				}
			}else{
				if(fx.imgA){
					url = fx.imgA
					w = imgAW
					h = imgAH
				}else if(fx.imgAMedium){
					url = fx.imgAMedium
					w = imgAMediumW
					h = imgAMediumH
				}else{
					url = fx.imgASmall
					w = imgASmallW
					h = imgASmallH
				}
			}
			setImgA({url, w, h})
		}
		window.addEventListener('resize', resize, tools.passiveListener);
		resize()
		return () => {
			window.removeEventListener('resize', resize);
		}
	}, [fx.imgA, fx.imgAMedium, fx.imgASmall, imgAW, imgAH, imgAMediumW, imgAMediumH, imgASmallW, imgASmallH]);
	useEffect(()=>{
		if(imgA){
			/* To optimize by screen size and for compotible with Android, this code
			* should be like next, but it not working in Epiphany browser (WebKit)
			* from Arch repo (but from webkit.org download page it works ok).
			*/
			return imgLoading({
				url: imgA.url,
				width: imgA.w,
				height: imgA.h,
				fun: (imageBitmap)=>{
					if(imageBitmap){
						const tex = new THREE.CanvasTexture( imageBitmap );
						setBgTexture(tex)
					}
					readyFun()
				}
			})
			/* To work in Epiphany browser (WebKit) from Arch repo this code code
			 * should be like next, but it is not optimized by screen size and
			 * have issue in Android.
			return textureLoading({
				url: imgA.url,
				fun: (tex)=>{
					if(tex){
						setBgTexture(tex)
					}
					readyFun()
				}
			})
			*/
		}
	}, [imgA, readyFun])
	useEffect(() => {
		if(material){
			material.uniforms.resolution.value.x = size.width;
			material.uniforms.resolution.value.y = size.height;
		}
	}, [size.width, size.height, material])
	useEffect(() => {
		if(geometry){
			const ratio =  size.width / size.height;
			const bgDH = Math.abs(camera.position.z - position.z) * Math.tan(Math.PI/180*camera.fov/2);
			const bgDW = bgDH * ratio;
			const vertices = new Float32Array( [
				-bgDW, -bgDH, 0,
				bgDW, -bgDH,  0,
				bgDW,  bgDH,  0,

				bgDW,  bgDH,  0,
				-bgDW,  bgDH, 0,
				-bgDW, -bgDH, 0
			] );
			geometry.setAttribute( 'position', new THREE.BufferAttribute( vertices, 3 ) );
		}
	}, [size.width, size.height, camera, position.z, geometry])
	useEffect(()=>{
		if(material){
			const p = []
			if(fx.colorA){
				p.push({})
				const pp = p[p.length - 1]
				pp.col = new THREE.Color(fx.colorA)
				pp.col.getHSL(pp)
			}
			if(fx.colorAA){
				p.push({})
				const pp = p[p.length - 1]
				pp.col = new THREE.Color(fx.colorAA)
				pp.col.getHSL(pp)
			}
			if(fx.colorAB){
				p.push({})
				const pp = p[p.length - 1]
				pp.col = new THREE.Color(fx.colorAB)
				pp.col.getHSL(pp)
			}
			if(p.length > 1){
				const isNeedHSL = fx.mixMode === 'color'
				return frameRun((t, fadeOut)=>{
					const pT = t*0.07
					const pI1 = Math.floor(pT % p.length)
					const pI2 = pI1 < p.length - 1 ? pI1 + 1 : 0
					const a = pT % 1
					const h = p[pI1].h + (p[pI2].h - p[pI1].h) * a
					const s = p[pI1].s + (p[pI2].s - p[pI1].s) * a
					const l = p[pI1].l + (p[pI2].l - p[pI1].l) * a
					material.uniforms.bgColor.value.setHSL(h, s, l)
					if(isNeedHSL){
						material.uniforms.bgColorHSL.value.set(h, s, l)
					}
				})
			}else if(p.length === 1){
				material.uniforms.bgColor.value = p[0].col
				material.uniforms.bgColorHSL.value.set(p[0].h, p[0].s, p[0].l)
			}else{
				material.uniforms.bgColor.value.set(0xffffff)
				material.uniforms.bgColorHSL.value.set(0,0,1)
			}
		}
	}, [frameRun, material, fx.colorA, fx.colorAA, fx.colorAB, fx.mixMode])

	useEffect(()=>{
		if(material){
			return frameRun((t, fadeOut)=>{
				material.uniforms.time.value = t
			})
		}
	}, [frameRun, material])
	useEffect(()=>{
		if(material && fx.gradient){
			return frameRun((t, fadeOut)=>{
				material.uniforms.sFactor.value = (window.scrollY - fx.sectionPos)/window.innerHeight
				if(material.uniforms.sFactor.value < 0){
					material.uniforms.sFactor.value = 0
				}else if(material.uniforms.sFactor.value > 1){
					material.uniforms.sFactor.value = 1
				}
			})
		}
	}, [frameRun, material, fx.gradient, fx.sectionPos])
	useEffect(()=>{
		if(material){
			if(isZoomOnScroll){
				return frameRun((t, fadeOut)=>{
					const sp = fx.scrollProgress();
					material.uniforms.zoom.value = 1 + sp * 0.3
				})
			}else if(isZoomInfinity){
				const zoomMax = 1.1
				const zoomL = 12
				const fadeL = 3
				const cycleL = 2 * (zoomL - fadeL)
				const opaqueL = zoomL - fadeL
				const zoomAdd = zoomMax - 1
				const zoomsDelta = cycleL - opaqueL
				const zoomFuncs = [
					(zoomMax, zoomAdd, zoomProgr) => (1 + zoomAdd * zoomProgr),
					(zoomMax, zoomAdd, zoomProgr) => (zoomMax - zoomAdd * zoomProgr)
				]
				const directions = [
					{x:0, y:0},
					{x:0, y:1},
					{x:0, y:-1},
					{x:1, y:1},
					{x:1, y:-1},
					{x:-1, y:1},
					{x:-1, y:-1}
				]
				const rndArr = (r, arr)=>{
					return arr[Math.floor(arr.length * r)]
				}
				const calcState = (t, rnd)=>{
					const cycleTime = t % cycleL
					const zoomProgr = Math.min(1, cycleTime/zoomL)
					const fadeOutProgr = Math.min(1, Math.max(0, (cycleTime - opaqueL)/fadeL))
					const move = (zoomProgr * zoomAdd)/4
					return {
						fadeOut: fadeOutProgr,
						zoom: rnd.zoomFunc(zoomMax, zoomAdd, zoomProgr),
						dx: rnd.dir.x * move,
						dy: rnd.dir.y * move
					}
				}
				const rnd1 = {cycle: false, zoomRnd: false, moveRnd: false}
				const rnd2 = {...rnd1}

				const rnd = (r, cycle)=>{
					if(r.cycle !== cycle){
						r.cycle = cycle
						r.zoomFunc = rndArr(Math.random(), zoomFuncs)
						r.dir = rndArr(Math.random(), directions)
					}
				}
				return frameRun((t, fadeOut)=>{
					rnd(rnd1, Math.ceil(t / cycleL))
					const state1 = calcState(t, rnd1)
					const t2 = t + zoomsDelta
					rnd(rnd2, Math.ceil(t2 / cycleL))
					const state2 = calcState(t2, rnd2)
					const sp = fx.scrollProgress();
					const spZoom = sp * 0.2;
					material.uniforms.zoom1.value = state1.zoom + spZoom;
					material.uniforms.zoom2.value = state2.zoom + spZoom;
					material.uniforms.dx1.value = state1.dx;
					material.uniforms.dx2.value = state2.dx;
					material.uniforms.dy1.value = state1.dy;
					material.uniforms.dy2.value = state2.dy;
					material.uniforms.zoomMix.value = state1.fadeOut > state2.fadeOut ? state1.fadeOut : 1 - state2.fadeOut
				})
			}
		}
	}, [frameRun, material, fx, isZoomInfinity, isZoomOnScroll])
	return (
		<>
			{isMouseEffect && (
				<>
					{isTail && material && (
						<MouseTail materialRef={material} />
					)}
					{isParticles && material && (
						<MouseParticles materialRef={material} pCount={pCount} />
					)}
					{isDistort && material && (
						<MouseDistort materialRef={material} />
					)}
				</>
			)}
		</>
	)
}
export {Background};
